import React, { useState, useEffect, useRef } from 'react';
import { api } from '../../services/api';
import { parseIterationResponse } from '../../services/iterationResponse';

interface Props {
  strategyId?: string;
  onComplete: () => void;
}

const Module6Actionables: React.FC<Props> = ({ strategyId, onComplete }) => {
  const [actionablesData, setActionablesData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  
  // Unified global chat history and input
  const [chatInput, setChatInput] = useState('');
  const [chatHistory, setChatHistory] = useState<{ role: 'user' | 'assistant'; content: string }[]>([]);
  const chatEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const loadExisting = async () => {
      setLoading(true);
      try {
        const res = await api.get(`/strategies/${strategyId}`);
        const data = res.data;
        if (data && data.module_6_actionables) {
          let parsed = data.module_6_actionables;
          if (typeof parsed === 'string') {
            try { parsed = JSON.parse(parsed); } catch(e){}
          }
          if (parsed && parsed.tareas && parsed.tareas.length > 0) {
            setActionablesData(parsed);
          }
        }
      } catch (err) {
        console.error("Error cargando accionables existentes:", err);
      } finally {
        setLoading(false);
      }
    };
    if (strategyId) {
      loadExisting();
    }
  }, [strategyId]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatHistory]);

  const generateActionables = async () => {
    setLoading(true);
    try {
      const res = await api.post(`/strategies/${strategyId}/modules/6/process`, {});
      let data = res.data;
      if (data.module_6_actionables) {
        setActionablesData(typeof data.module_6_actionables === 'string' ? JSON.parse(data.module_6_actionables) : data.module_6_actionables);
      }
    } catch (err) {
      alert('Error: ' + err);
    } finally {
      setLoading(false);
    }
  };

  const handleTaskFieldChange = (idx: number, field: string, value: any) => {
    const updated = { ...actionablesData };
    if (field === 'canales') {
      updated.tareas[idx].canales = value.split(',').map((s: string) => s.trim());
    } else if (field === 'kpis') {
      updated.tareas[idx].kpis = value.split(',').map((s: string) => s.trim());
    } else {
      updated.tareas[idx][field] = value;
    }
    setActionablesData(updated);
  };

  const handleSendChat = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!chatInput.trim() || loading) return;

    const userMsg = chatInput;
    setChatInput('');
    setChatHistory(prev => [...prev, { role: 'user', content: userMsg }]);
    setLoading(true);

    try {
      const res = await api.post(`/strategies/${strategyId}/modules/6/chat`, {
        message: userMsg
      });

      const result = parseIterationResponse<any>(res.data);

      if (result.state && Array.isArray(result.state.tareas)) {
        setActionablesData((prev: any) => ({ ...(prev || {}), ...result.state }));
      }

      const replyText = result.reply || 'Accionables actualizados';
      setChatHistory(prev => [...prev, { role: 'assistant', content: replyText }]);
      if (result.alertDetails.length > 0 && !result.reply.includes(result.alertDetails[0].message)) {
        setChatHistory(prev => [...prev, {
          role: 'assistant',
          content: result.alertDetails.map((alert) => alert.message).join(' '),
        }]);
      }
    } catch (err) {
      console.error(err);
      setChatHistory(prev => [...prev, { role: 'assistant', content: 'Error al actualizar los accionables.' }]);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    setLoading(true);
    try {
      await api.put(`/strategies/${strategyId}/modules/6/update-state`, actionablesData);
      onComplete();
    } catch (err) {
      alert('Error: ' + err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.container}>
      <h2 style={styles.title}>Módulo VI: Accionables</h2>
      <p style={styles.description}>Traducción final de la estrategia en ideas accionables. El Agente ACCIÓN provee bajadas tácticas para el equipo creativo.</p>

      {!actionablesData ? (
        <button style={styles.primaryButton} onClick={generateActionables} disabled={loading}>
          {loading ? 'Generando Accionables...' : 'Generar Accionables'}
        </button>
      ) : (
        <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            {actionablesData.tareas && actionablesData.tareas.map((tarea: any, idx: number) => (
              <div key={idx} style={{ ...styles.card, background: 'var(--bg-hover)' }}>
                <label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '6px' }}>Accionable {idx + 1}: Título</label>
                <input
                  type="text"
                  style={{ padding: '10px 12px', border: '1px solid var(--border)', borderRadius: '4px', background: 'var(--bg-card)', color: 'var(--text-primary)', width: '100%', fontSize: '16px', fontWeight: 'bold', marginBottom: '16px', fontFamily: 'inherit' }}
                  value={tarea.titulo || ''}
                  onChange={(e) => handleTaskFieldChange(idx, 'titulo', e.target.value)}
                  placeholder="Título del accionable..."
                />
                
                <label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '6px' }}>Explicación / Descripción</label>
                <textarea
                  style={{ padding: '12px', border: '1px solid var(--border)', borderRadius: '4px', fontSize: '14px', fontFamily: 'inherit', background: 'var(--bg-card)', color: 'var(--text-primary)', width: '100%', resize: 'vertical', marginBottom: '16px' }}
                  rows={3}
                  value={tarea.explicacion || tarea.descripcion || ''}
                  onChange={(e) => handleTaskFieldChange(idx, 'explicacion', e.target.value)}
                  placeholder="Detalles sobre el accionable..."
                />
                
                <label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '6px' }}>Dirección Integrada</label>
                <textarea
                  style={{ padding: '12px', border: '1px solid var(--border)', borderRadius: '4px', fontSize: '14px', fontFamily: 'inherit', background: 'var(--bg-card)', color: 'var(--text-primary)', width: '100%', resize: 'vertical', marginBottom: '16px' }}
                  rows={2}
                  value={tarea.direccion_integrada || ''}
                  onChange={(e) => handleTaskFieldChange(idx, 'direccion_integrada', e.target.value)}
                  placeholder="Dirección estratégica integrada..."
                />

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <div>
                    <label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-muted)' }}>CANALES (separados por coma)</label>
                    <input
                      type="text"
                      style={{ padding: '10px 12px', border: '1px solid var(--border)', borderRadius: '4px', background: 'var(--bg-card)', color: 'var(--text-primary)', width: '100%', fontSize: '13px', marginTop: '6px', fontFamily: 'inherit' }}
                      value={Array.isArray(tarea.canales) ? tarea.canales.join(', ') : tarea.canales || ''}
                      onChange={(e) => handleTaskFieldChange(idx, 'canales', e.target.value)}
                      placeholder="Canal A, Canal B..."
                    />
                  </div>
                  <div>
                    <label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-muted)' }}>ROL DE CANALES</label>
                    <textarea
                      style={{ padding: '10px 12px', border: '1px solid var(--border)', borderRadius: '4px', background: 'var(--bg-card)', color: 'var(--text-primary)', width: '100%', fontSize: '13px', marginTop: '6px', resize: 'vertical', fontFamily: 'inherit' }}
                      rows={2}
                      value={tarea.rol_canales || ''}
                      onChange={(e) => handleTaskFieldChange(idx, 'rol_canales', e.target.value)}
                      placeholder="Objetivo o función del canal..."
                    />
                  </div>
                  <div>
                    <label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-muted)' }}>OBJETIVOS</label>
                    <textarea
                      style={{ padding: '10px 12px', border: '1px solid var(--border)', borderRadius: '4px', background: 'var(--bg-card)', color: 'var(--text-primary)', width: '100%', fontSize: '13px', marginTop: '6px', resize: 'vertical', fontFamily: 'inherit' }}
                      rows={2}
                      value={tarea.objetivos || ''}
                      onChange={(e) => handleTaskFieldChange(idx, 'objetivos', e.target.value)}
                      placeholder="Objetivos específicos de la tarea..."
                    />
                  </div>
                  <div>
                    <label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-muted)' }}>KPIS (separados por coma)</label>
                    <input
                      type="text"
                      style={{ padding: '10px 12px', border: '1px solid var(--border)', borderRadius: '4px', background: 'var(--bg-card)', color: 'var(--text-primary)', width: '100%', fontSize: '13px', marginTop: '6px', fontFamily: 'inherit' }}
                      value={Array.isArray(tarea.kpis) ? tarea.kpis.join(', ') : tarea.kpis || tarea.kpi_relacionado || ''}
                      onChange={(e) => handleTaskFieldChange(idx, 'kpis', e.target.value)}
                      placeholder="KPI 1, KPI 2..."
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Unified Global Chat */}
          <div style={{ marginTop: '32px', borderTop: '1px solid var(--border)', paddingTop: '24px' }}>
            <h3 style={{ fontSize: '16px', color: 'var(--text-primary)', marginBottom: '16px', fontWeight: 600 }}>Chat de Iteración Estratégica (Módulo VI)</h3>
            <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '16px' }}>Puedes pedirle al agente que añada, elimine o modifique las tareas tácticas (ej: "añade una tarea para Instagram" o "cambia el enfoque del accionable 2").</p>
            
            {(chatHistory.length > 0) && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '16px', maxHeight: '300px', overflowY: 'auto', border: '1px solid var(--border)', padding: '16px', borderRadius: '6px', background: 'var(--bg-card)' }}>
                {chatHistory.map((msg, i) => (
                  <div key={i} style={{ 
                    alignSelf: msg.role === 'user' ? 'flex-end' : 'flex-start',
                    background: msg.role === 'user' ? 'var(--accent)' : 'var(--bg-hover)',
                    color: msg.role === 'user' ? '#fff' : 'var(--text-primary)',
                    padding: '10px 14px',
                    borderRadius: '8px',
                    maxWidth: '80%'
                  }}>
                    <p style={{ margin: 0, fontSize: '13px' }}>{msg.content}</p>
                  </div>
                ))}
                <div ref={chatEndRef} />
              </div>
            )}

            <form onSubmit={handleSendChat} style={{ display: 'flex', gap: '12px' }}>
              <input 
                type="text" 
                style={{ padding: '12px', border: '1px solid var(--border)', borderRadius: '4px', fontSize: '14px', background: 'transparent', color: 'var(--text-primary)', flex: 1, fontFamily: 'inherit' }} 
                value={chatInput} 
                onChange={e => setChatInput(e.target.value)} 
                placeholder="Ej: Añade un nuevo accionable enfocado en TikTok..." 
                disabled={loading}
              />
              <button type="submit" style={styles.primaryButton} disabled={loading || !chatInput.trim()}>
                {loading ? '...' : 'Enviar'}
              </button>
            </form>
          </div>
          
          <div style={styles.actionsRow}>
            <button style={{ ...styles.primaryButton, background: 'var(--success)' }} onClick={handleApprove} disabled={loading}>
              {loading ? 'Guardando...' : 'Enviar'}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: { display: 'flex', flexDirection: 'column', gap: '16px' },
  title: { fontSize: '24px', color: 'var(--text-primary)', marginBottom: '8px' },
  description: { color: 'var(--text-secondary)', marginBottom: '24px' },
  input: { padding: '12px', border: '1px solid var(--border)', borderRadius: '4px', fontSize: '14px', fontFamily: 'inherit', background: 'transparent', color: 'var(--text-primary)' },
  primaryButton: { background: 'var(--accent)', color: '#fff', padding: '12px 24px', border: 'none', borderRadius: '4px', fontWeight: 600, cursor: 'pointer' },
  card: { padding: '24px', border: '1px solid var(--border)', borderRadius: '8px', background: 'var(--bg-card)', color: 'var(--text-primary)' },
  cardTitle: { fontSize: '14px', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: '16px', letterSpacing: '0.05em' },
  list: { paddingLeft: '20px', margin: 0 },
  listItem: { fontSize: '14px', lineHeight: 1.6, marginBottom: '8px' },
  cardContent: { fontSize: '14px', margin: 0, lineHeight: 1.6 },
  actionsRow: { display: 'flex', justifyContent: 'flex-end', gap: '16px', marginTop: '24px' },
  secondaryButton: { background: 'transparent', border: '1px solid var(--border)', color: 'var(--text-primary)', padding: '12px 24px', borderRadius: '4px', fontWeight: 600, cursor: 'pointer' }
};

export default Module6Actionables;
